import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  StyledList,
  EnhancedGoBackButton,
  SplitSection
} from '../../components';
import { PREPARING_INTERVIEW_CONTENT } from '../../data/content/how-to-join';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const PreparingInterviewPage = ({ setRefs }) => {
  const overviewRef = useRef(null);
  const whatWereLookingForRef = useRef(null);
  const whatWereLookingForItemsRef = useRef(null);
  const gettingReadyRef = useRef(null);
  const gettingReadyItemsRef = useRef(null);
  const whatWeAskRef = useRef(null);
  const whatWeAskItemsRef = useRef(null);
  const additionalInfoRef = useRef(null);
  const afterInterviewRef = useRef(null);
  const afterInterviewStepsRef = useRef(null);
  const afterInterviewClosingRef = useRef(null);
  const footerRef = useRef(null);

  const refs = useMemo(
    () => ({
      overview: overviewRef,
      whatWereLookingFor: whatWereLookingForRef,
      whatWereLookingForItems: whatWereLookingForItemsRef,
      gettingReady: gettingReadyRef,
      gettingReadyItems: gettingReadyItemsRef,
      whatWeAsk: whatWeAskRef,
      whatWeAskItems: whatWeAskItemsRef,
      additionalInfo: additionalInfoRef,
      afterInterview: afterInterviewRef,
      afterInterviewSteps: afterInterviewStepsRef,
      afterInterviewClosing: afterInterviewClosingRef,
      footer: footerRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.preparingInterview.title}
        content={seoData.preparingInterview.content}
        keywords={seoData.preparingInterview.keywords}
        canonical={seoData.preparingInterview.canonical}
        ogImage={seoData.preparingInterview.ogImage}
      />
      {/* Back Button */}}
      <EnhancedGoBackButton
        backTo={ROUTES.HOW_TO_JOIN.INTERVIEW}
        backText='Back to Interview Process'
      />
      {/* Main "Preparing for Interview" section */}
      <SectionContent
        id='hero'
        ref={refs.overview}
        title={PREPARING_INTERVIEW_CONTENT.TITLE}
        titleVariant='TitleLarge'
        titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={PREPARING_INTERVIEW_CONTENT.INTRO.map((text, index, array) => ({
          text,
          marginBottom:
            index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />
      {/* What We're Looking For Section */}
      <SectionContent
        id='whatWereLookingFor'
        ref={refs.whatWereLookingFor}
        title={PREPARING_INTERVIEW_CONTENT.WHAT_WERE_LOOKING_FOR.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: PREPARING_INTERVIEW_CONTENT.WHAT_WERE_LOOKING_FOR.TEXT,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        ref={refs.whatWereLookingForItems}
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          items={PREPARING_INTERVIEW_CONTENT.WHAT_WERE_LOOKING_FOR.ITEMS}
        />
      </Box>
      {/* Getting Ready for Your Interview Section */}
      <SectionContent
        id='gettingReady'
        ref={refs.gettingReady}
        title={PREPARING_INTERVIEW_CONTENT.GETTING_READY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: PREPARING_INTERVIEW_CONTENT.GETTING_READY.TEXT,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        ref={refs.gettingReadyItems}
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={PREPARING_INTERVIEW_CONTENT.GETTING_READY.ITEMS} />
      </Box>
      {/* What We Might Ask You Section */}
      <SectionContent
        id='whatWeAsk'
        ref={refs.whatWeAsk}
        title={PREPARING_INTERVIEW_CONTENT.WHAT_WE_ASK.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: PREPARING_INTERVIEW_CONTENT.WHAT_WE_ASK.TEXT,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        ref={refs.whatWeAskItems}
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={PREPARING_INTERVIEW_CONTENT.WHAT_WE_ASK.ITEMS} />
      </Box>
      <SectionContent
        id='additionalInfo'
        ref={refs.additionalInfo}
        initialSpacing={false}
        text={[
          {
            text: PREPARING_INTERVIEW_CONTENT.WHAT_WE_ASK.ADDITIONAL_TEXT,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* After the Interview Section */}
      <SectionContent
        id='afterInterview'
        ref={refs.afterInterview}
        title={PREPARING_INTERVIEW_CONTENT.AFTER_INTERVIEW.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: PREPARING_INTERVIEW_CONTENT.AFTER_INTERVIEW.TEXT,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Split Section for two StyledLists */}
      <SplitSection
        leftContent={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <StyledList
              variant='blue'
              items={PREPARING_INTERVIEW_CONTENT.AFTER_INTERVIEW.STEPS_LEFT}
            />
          </Box>
        }
        rightContent={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <StyledList
              variant='blue'
              items={PREPARING_INTERVIEW_CONTENT.AFTER_INTERVIEW.STEPS_RIGHT}
            />
          </Box>
        }
        sx={{ mb: SECTION_SPACING }}
        equalHeight={true}
      />
      <SectionContent
        id='afterInterviewClosing'
        ref={refs.afterInterviewClosing}
        initialSpacing={false}
        text={PREPARING_INTERVIEW_CONTENT.AFTER_INTERVIEW.CLOSING_TEXT.map(
          (text, index, array) => ({
            text,
            marginBottom:
              index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Footer note with separator line */}
      <Box
        ref={refs.footer}
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {PREPARING_INTERVIEW_CONTENT.FOOTER_TEXT}
        </Typography>
      </Box>
    </>
  );
};

export default PreparingInterviewPage;
